import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input } from 'components/common';
import RichTextEditor from 'components/admin/RichTextEditor';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { createLeader, getAdminLeaderById, updateLeader } from 'apis/adminLeaders';
import { getAdminLeaderCategories } from 'apis/adminLeaderCategories';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import { normalizeLeaderPositions } from 'utils/leaderHelpers';
import '../../../styles/admin-shared.scss';

const createInformationItem = (languages, sortOrder = 1) => ({
    sortOrder,
    isActive: true,
    translation: languages.map((language) => ({ languageId: Number(language.id), title: '', description: '' })),
});

const normalizeInformationTranslations = (item, languages) => {
    const rawTranslations = item?.translation || item?.translations || item?.leaderInformationItemTranslations || item?.LeaderInformationItemTranslations || [];
    return languages.map((language) => {
        const match = rawTranslations.find((translation) => Number(translation.languageId ?? translation.LanguageId) === Number(language.id));
        const itemLanguageCode = item?.languageCode ?? item?.LanguageCode;
        const isFlattenedTranslation = !rawTranslations.length && (!itemLanguageCode || itemLanguageCode === language.code);
        return {
            ...match,
            languageId: Number(language.id),
            title: match?.title ?? match?.Title ?? (isFlattenedTranslation ? item?.title ?? item?.Title : '') ?? '',
            description: match?.description ?? match?.Description ?? (isFlattenedTranslation ? item?.description ?? item?.Description : '') ?? '',
        };
    });
};

const LeadersForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({ categoryIds: [], name: '', slug: '', image: '', sortOrder: 0, isActive: true, translation: [], informationItems: [] });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery('leader-categories-options', { pageSize: 1000 }, getAdminLeaderCategories);
    const categories = categoriesData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery('leaders', id, getAdminLeaderById);
    const createMutation = useAdminCreateMutation('leaders', createLeader);
    const updateMutation = useAdminUpdateMutation('leaders', updateLeader);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, positions: [], education: '', summary: '', description: '' })),
                informationItems: prev.informationItems.length ? prev.informationItems : [createInformationItem(languages)],
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail && languages.length) {
            const translations = detail.translation || detail.translations || [];
            const informationItems = detail.informationItems || detail.InformationItems || [];
            setFormData({
                ...detail,
                categoryIds: Array.isArray(detail.categoryIds) && detail.categoryIds.length
                    ? detail.categoryIds.map(Number)
                    : Array.isArray(detail.categories) && detail.categories.length
                        ? detail.categories.map((category) => Number(category.categoryId ?? category.id)).filter(Number.isFinite)
                        : detail.categoryId ? [Number(detail.categoryId)] : [],
                translation: translations.map((translation) => ({
                    ...translation,
                    positions: normalizeLeaderPositions(translation.positions),
                })),
                informationItems: informationItems
                    .map((item) => ({
                        ...item,
                        sortOrder: Number(item.sortOrder ?? item.SortOrder) || 0,
                        isActive: item.isActive ?? item.IsActive ?? true,
                        translation: normalizeInformationTranslations(item, languages),
                    }))
                    .sort((first, second) => first.sortOrder - second.sortOrder),
            });
        }
    }, [detail, languages]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'T?i d? li?u th?t b?i'); }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => Number(t.languageId) === Number(langId) ? { ...t, [field]: value } : t) }));
    };

    const handleCategoryToggle = (categoryId) => {
        const numericId = Number(categoryId);
        setFormData((previous) => ({
            ...previous,
            categoryIds: previous.categoryIds.includes(numericId)
                ? previous.categoryIds.filter((id) => id !== numericId)
                : [...previous.categoryIds, numericId],
        }));
        setErrors((previous) => ({ ...previous, categoryIds: '' }));
    };

    const handleInformationItemChange = (index, field, value) => {
        setFormData((previous) => ({
            ...previous,
            informationItems: previous.informationItems.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
        }));
    };

    const handleInformationTranslationChange = (index, languageId, field, value) => {
        setFormData((previous) => ({
            ...previous,
            informationItems: previous.informationItems.map((item, itemIndex) => {
                if (itemIndex !== index) return item;
                const hasLanguage = item.translation.some((translation) => Number(translation.languageId) === Number(languageId));
                const translation = hasLanguage
                    ? item.translation.map((entry) => Number(entry.languageId) === Number(languageId) ? { ...entry, [field]: value } : entry)
                    : [...item.translation, { languageId: Number(languageId), title: '', description: '', [field]: value }];
                return { ...item, translation };
            }),
        }));
    };

    const addInformationItem = () => {
        setFormData((previous) => ({
            ...previous,
            informationItems: [
                ...previous.informationItems,
                createInformationItem(languages, Math.max(0, ...previous.informationItems.map((item) => Number(item.sortOrder) || 0)) + 1),
            ],
        }));
    };

    const removeInformationItem = (index) => {
        setFormData((previous) => ({
            ...previous,
            informationItems: previous.informationItems.filter((_, itemIndex) => itemIndex !== index),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!formData.categoryIds.length) {
                setErrors((previous) => ({ ...previous, categoryIds: 'Vui lòng chọn ít nhất một danh mục' }));
                return;
            }
            const payload = {
                ...formData,
                categoryIds: formData.categoryIds.map(Number),
                categoryId: Number(formData.categoryIds[0]),
                sortOrder: Number(formData.sortOrder),
                translation: formData.translation.map((translation) => ({
                    ...translation,
                    positions: normalizeLeaderPositions(translation.positions),
                })),
                informationItems: formData.informationItems.map((item) => ({
                    sortOrder: Number(item.sortOrder) || 0,
                    isActive: Boolean(item.isActive),
                    translation: item.translation.map((translation) => ({
                        languageId: Number(translation.languageId),
                        title: translation.title || '',
                        description: translation.description || '',
                    })),
                })),
            };
            const res = id ? await updateMutation.mutateAsync({ id, data: payload }) : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/leaders'); }
            else { if (res.messageError) setErrors(res.messageError); else toast.error(res.message || 'Lưu thất bại'); }
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Lãnh đạo' : 'Thêm Lãnh đạo'} subtitle="Nhập hồ sơ cơ bản và nội dung bản dịch của lãnh đạo" />
            <form className="form-card leader-information-form" onSubmit={handleSubmit}>
                <p className="form-section-title">Thông tin Lãnh đạo</p>
                <div className="form-row">
                    <div className="leader-name-field"><Input label="Tên *" name="name" value={formData.name} onChange={handleChange} required error={errors.name} /></div>
                    <div className="input-group leader-category-picker">
                        <label>Danh mục <span className="required">*</span></label>
                        <div className={`leader-category-picker__options ${errors.categoryIds ? 'error' : ''}`}>
                            {categories.map((category) => {
                                const activeLangId = languages[activeTab]?.id;
                                const translation = category.translations?.find((item) => item.languageId === activeLangId) || category.translations?.[0];
                                const label = translation?.name || translation?.title || category.name || `Danh mục ${category.id}`;
                                const checked = formData.categoryIds.includes(Number(category.id));
                                return <label key={category.id} className={`leader-category-picker__option ${checked ? 'selected' : ''}`}>
                                    <input type="checkbox" checked={checked} onChange={() => handleCategoryToggle(category.id)} />
                                    <span className="leader-category-picker__check" aria-hidden="true" />
                                    <span className="leader-category-picker__label">{label}</span>
                                </label>;
                            })}
                        </div>
                        <small className="field-help">Có thể chọn nhiều danh mục cho cùng một lãnh đạo.</small>
                        {errors.categoryIds && <span className="error-message">{errors.categoryIds}</span>}
                    </div>
                </div>
                <div className="form-row">
                    <Input label="Slug" name="slug" value={formData.slug} onChange={handleChange} />
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                <ImageUploadField
                    label="Ảnh chân dung lãnh đạo"
                    value={formData.image}
                    folder="leaders"
                    onChange={(image) => setFormData(prev => ({ ...prev, image }))}
                />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{lang.name}</button>)}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => Number(t.languageId) === Number(lang.id)) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Chức vụ (ngăn cách bằng dấu phẩy)" value={normalizeLeaderPositions(t.positions).join(', ')} onChange={e => handleTranslationChange(lang.id, 'positions', normalizeLeaderPositions(e.target.value))} />
                                        <Input label="Học vấn" value={t.education || ''} onChange={e => handleTranslationChange(lang.id, 'education', e.target.value)} />
                                        <p className="form-section-title">Thông tin lãnh đạo</p>
                                        {formData.informationItems.map((item, itemIndex) => {
                                            const informationTranslation = item.translation.find((translation) => Number(translation.languageId) === Number(lang.id)) || {};
                                            return <div className="admin-nested-block information-item-card" key={item.id || itemIndex}>
                                                <div className="information-item-card__header">
                                                    <div><span className="information-item-card__number">{String(itemIndex + 1).padStart(2, '0')}</span><strong>Phần thông tin {itemIndex + 1}</strong></div>
                                                    <button type="button" className="information-item-card__remove" onClick={() => removeInformationItem(itemIndex)} title="Xóa phần thông tin"><FiTrash2 size={16} /><span>Xóa</span></button>
                                                </div>
                                                <div className="information-item-card__settings">
                                                    <Input label="Thứ tự" type="number" value={item.sortOrder} onChange={(event) => handleInformationItemChange(itemIndex, 'sortOrder', event.target.value)} />
                                                    <div className="information-item-card__status"><span>Trạng thái</span><Checkbox label="Hoạt động" checked={item.isActive} onChange={(event) => handleInformationItemChange(itemIndex, 'isActive', event.target.checked)} /></div>
                                                </div>
                                                <div className="information-item-card__translation">
                                                    <Input label={`Tiêu đề (${lang.name})`} value={informationTranslation.title || ''} onChange={(event) => handleInformationTranslationChange(itemIndex, lang.id, 'title', event.target.value)} />
                                                    <div className="input-group">
                                                        <label>Nội dung ({lang.name})</label>
                                                        <RichTextEditor key={`leader-information-${itemIndex}-${lang.id}`} value={informationTranslation.description || ''} onChange={(value) => handleInformationTranslationChange(itemIndex, lang.id, 'description', value)} placeholder="Nhập nội dung phần thông tin..." />
                                                    </div>
                                                </div>
                                            </div>;
                                        })}
                                        <Button type="button" variant="secondary" className="information-item-add" onClick={addInformationItem}><FiPlus size={16} /> Thêm phần thông tin</Button>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật Lãnh đạo' : 'Thêm Lãnh đạo'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/leaders')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default LeadersForm;
